import { css, sva } from '@repo/pandacss/css';
import type { SystemStyleObject } from '@repo/pandacss/types';
import type { Likelihood } from '@/features/approval-odds/constants';

export const APPROVAL_ODDS_CSS_VARS = {
  labelColor: '--approval-odds-label-color',
} as const;

/**
 * This query is used to determine if the approval odds should be displayed in a narrow driver.
 * The value is a "magic number" that is roughly the max width + estimated label overflow. (312px + 30px)
 * It needs to be written as such to avoid issues with the CSS static analyzer.
 */
const narrowDriverQuery = `@container product-card-driver (width < 342px)`;

export const approvalLabelGridClass = css({
  maxWidth: `[312px]`,
  gridRow: '1',
  gridColumn: 'span 5',
  mb: '1',
});

/** Set styles for various parts of approval odds based on likelihood */
export const approvalOddsSlotRecipe = sva({
  slots: ['container', 'labelContainer', 'label'],
  base: {
    container: {
      position: 'relative',
      gridTemplateRows: 'repeat(3, auto)',
    },
    labelContainer: {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',
      textAlign: 'center',
      mb: '2',
      gridColumnStart: 'auto',
      transform: 'auto',
    },
    label: {
      whiteSpace: 'nowrap',
      borderRadius: 'sm',
      py: '1',
      px: '[.75rem]',
      width: '[fit-content]',
      color: 'common.white',
      zIndex: '[999]',
      bgColor: `var(${APPROVAL_ODDS_CSS_VARS.labelColor})`,
      _after: {
        bgColor: `var(${APPROVAL_ODDS_CSS_VARS.labelColor})`,
        content: '""',
        position: 'absolute',
        boxSize: '[0.625rem]',
        left: '[50%]',
        transform: 'rotate(45deg) translateX(-50%)',
        transformOrigin: 'left',
        bottom: '[-0.25rem]',
      },
    },
  },
  variants: {
    likelihood: {
      Poor: {
        container: {
          [APPROVAL_ODDS_CSS_VARS.labelColor]: '{colors.reds.light}',
        },
        labelContainer: {
          gridColumnStart: 1,
          transform: 'translateX(calc(50% + {spacing.1}/2))',
        },
      },
      Fair: {
        container: {
          [APPROVAL_ODDS_CSS_VARS.labelColor]: '{colors.yellows.main}',
        },
        labelContainer: {
          gridColumnStart: 3,
        },
      },
      Good: {
        container: {
          [APPROVAL_ODDS_CSS_VARS.labelColor]: '{colors.primary.light}',
        },
        labelContainer: {
          gridColumnStart: 4,
        },
      },
      Excellent: {
        container: {
          [APPROVAL_ODDS_CSS_VARS.labelColor]: '{colors.greens.main}',
        },
        labelContainer: {
          gridColumnStart: 5,
          [narrowDriverQuery]: {
            justifyContent: 'end',
          },
        },
        label: {
          [narrowDriverQuery]: {
            _after: {
              right: '[calc(50% - .625rem / 2)]' as const,
            },
          },
        },
      },
    } satisfies LikelihoodVariants,
  },
});

// Typing for the slot recipe to ensure we're accounting for all likelihoods
type LikelihoodVariants = Record<
  Likelihood,
  Partial<Record<'container' | 'labelContainer' | 'label', SystemStyleObject>>
>;
